import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, MenuItem, Chip, FormControl, InputLabel, Select, OutlinedInput,
  Snackbar, Alert, Stack, Divider, InputAdornment, Checkbox, ListItemText,
  FormControlLabel, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon, Person as PersonIcon, Lock as LockIcon,
  ToggleOn as ToggleOnIcon, ToggleOff as ToggleOffIcon,
  FileDownload as FileDownloadIcon,
  Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const UserManagement = () => {
  const { user } = useAuth();
  const canReactivate = ['owner', 'manager'].includes(user?.role);
  const isOwner = user?.role === 'owner'; // Check if logged in user is owner
  const canSeeSalary = ['owner', 'manager', 'hr'].includes(user?.role); // Owner, Manager, HR can see salary
  const [users, setUsers] = useState([]);
  const [ROLES, setROLES] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMaximized, setDialogMaximized] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [statistics, setStatistics] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    firstName: '',
    lastName: '',
    role: 'actor',
    roles: [],
    employeeId: '',
    salary: '',
    gender: '',
    partTime: false,
    partTimeDaysPerMonth: '',
    phoneNumber: '',
    whatsappNumber: '',
    emergencyContactName: '',
    emergencyContactMobile: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankName: '',
    bankBranch: '',
    accountHolderName: '',
    isActive: true,
    dateOfJoining: '',
    lastWorkingDate: '',
    isRejoinee: false,
    employmentHistory: []
  });
  const [newPassword, setNewPassword] = useState('');
  const [showSalary, setShowSalary] = useState(false); // Toggle for showing/hiding salary

  useEffect(() => {
    fetchUsers();
    fetchStatistics();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/dropdowns');
      if (response.data.data.roles) {
        setROLES(response.data.data.roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Fallback to hardcoded roles if API fails
      setROLES([
        { value: 'owner', label: 'Owner', color: 'error', icon: '👑' },
        { value: 'manager', label: 'Manager', color: 'primary', icon: '📊' },
        { value: 'director', label: 'Director', color: 'secondary', icon: '🎬' },
        { value: 'editor', label: 'Editor', color: 'info', icon: '✂️' },
        { value: 'cinematographer', label: 'Cinematographer', color: 'success', icon: '🎥' },
        { value: 'actor', label: 'Actor', color: 'warning', icon: '🎭' },
        { value: 'writer', label: 'Writer', color: 'default', icon: '✍️' },
        { value: 'hr', label: 'HR', color: 'default', icon: '👥' }
      ]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users', {
        params: { search: searchTerm, role: roleFilter }
      });
      setUsers(response.data.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/users/statistics');
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleOpenDialog = (user = null) => {
    setShowSalary(false); // Reset salary visibility when opening dialog
    if (user) {
      setSelectedUser(user);
      // Ensure roles is always an array
      let userRoles = [];
      if (Array.isArray(user.roles)) {
        userRoles = user.roles;
      } else if (user.role) {
        userRoles = [user.role];
      }
      
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        fullName: user.fullName,
        role: user.role,
        roles: userRoles,
        employeeId: user.employeeId || '',
        salary: user.salary || '',
        gender: user.gender || '',
        partTime: user.partTime || false,
        partTimeDaysPerMonth: user.partTimeDaysPerMonth || '',
        phoneNumber: user.phoneNumber || '',
        whatsappNumber: user.whatsappNumber || '',
        emergencyContactName: user.emergencyContactName || '',
        emergencyContactMobile: user.emergencyContactMobile || '',
        bankAccountNumber: user.bankAccountNumber || '',
        bankIfscCode: user.bankIfscCode || '',
        bankName: user.bankName || '',
        bankBranch: user.bankBranch || '',
        accountHolderName: user.accountHolderName || '',
        isActive: user.isActive,
        dateOfJoining: user.dateOfJoining ? user.dateOfJoining.toString().slice(0, 10) : '',
        lastWorkingDate: user.lastWorkingDate ? user.lastWorkingDate.toString().slice(0, 10) : '',
        isRejoinee: !!user.isRejoinee,
        employmentHistory: parseHistory(user.employmentHistory)
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        fullName: '',
        role: '',
        roles: [],
        employeeId: '',
        salary: '',
        partTime: false,
        phoneNumber: '',
        whatsappNumber: '',
        emergencyContactName: '',
        emergencyContactMobile: '',
        bankAccountNumber: '',
        bankIfscCode: '',
        bankName: '',
        bankBranch: '',
        accountHolderName: '',
        isActive: true,
        dateOfJoining: '',
        lastWorkingDate: '',
        isRejoinee: false,
        employmentHistory: []
      });
    }
    setDialogOpen(true);
  };

  // Re-joinee prior-stint helpers
  const parseHistory = (h) => {
    if (Array.isArray(h)) return h;
    if (typeof h === 'string') { try { const p = JSON.parse(h); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };
  const addStint = () => setFormData((f) => ({ ...f, employmentHistory: [...parseHistory(f.employmentHistory), { joinedOn: '', relievedOn: '', designation: '', relievingReason: '', remarks: '' }] }));
  const updateStint = (i, key, val) => setFormData((f) => {
    const arr = parseHistory(f.employmentHistory).map((s, idx) => (idx === i ? { ...s, [key]: val } : s));
    return { ...f, employmentHistory: arr };
  });
  const removeStint = (i) => setFormData((f) => ({ ...f, employmentHistory: parseHistory(f.employmentHistory).filter((_, idx) => idx !== i) }));

  // Auto-populate username when firstName changes (for new users only)
  const handleFirstNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      firstName: value,
      // Only auto-populate username for new users
      username: !selectedUser ? `JBDE${value}` : prev.username
    }));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setDialogMaximized(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.firstName || formData.firstName.trim() === '') {
      alert('First Name is required');
      return;
    }
    
    if (!formData.username || formData.username.trim() === '') {
      alert('Username is required');
      return;
    }
    
    if (!formData.phoneNumber || formData.phoneNumber.trim() === '') {
      alert('Phone Number is required');
      return;
    }
    
    if (!formData.whatsappNumber || formData.whatsappNumber.trim() === '') {
      alert('WhatsApp Number is required');
      return;
    }
    
    if (!Array.isArray(formData.roles) || formData.roles.length === 0) {
      alert('At least one role must be assigned to the user');
      return;
    }
    
    if (!selectedUser && (!formData.password || formData.password.trim() === '')) {
      alert('Password is required for new users');
      return;
    }

    // Role assignment validation
    const allowedOwners = ['JBDESathiyaprabhu', 'JBDESridhar', 'JBDEKokilavani', 'JBDESumathi'];
    const allowedManagers = ['JBDESathiyaprabhu', 'JBDESridhar', 'JBDETamilan', 'JBDEDeepa'];

    if (formData.roles.includes('owner')) {
      if (!allowedOwners.includes(formData.username)) {
        alert(`❌ Access Restricted!\n\nOnly Sathiyaprabhu and Sridhar can be assigned the Owner role.\n\nCurrent user: ${formData.username}\nAllowed users: ${allowedOwners.join(', ')}`);
        return;
      }
    }

    if (formData.roles.includes('manager')) {
      if (!allowedManagers.includes(formData.username)) {
        alert(`❌ Access Restricted!\n\nOnly Sathiyaprabhu, Sridhar, and Tamilan can be assigned the Manager role.\n\nCurrent user: ${formData.username}\nAllowed users: ${allowedManagers.join(', ')}`);
        return;
      }
    }
    
    try {
      // Keep only prior stints that have at least one filled field
      const cleanHistory = parseHistory(formData.employmentHistory).filter(
        (s) => s.joinedOn || s.relievedOn || s.relievingReason || s.designation || s.remarks
      );
      // Auto-generate fullName from firstName and lastName
      const dataToSend = {
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`.trim() || formData.fullName,
        isRejoinee: !!formData.isRejoinee,
        employmentHistory: cleanHistory
      };
      
      if (selectedUser) {
        await api.put(`/users/${selectedUser.id}`, dataToSend);
      } else {
        await api.post('/users', dataToSend);
      }
      handleCloseDialog();
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.message || 'Error saving user');
    }
  };

  const handleDelete = async () => {
    try {
      // Permanently delete user
      await api.delete(`/users/${selectedUser.id}`);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStatistics();
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || 'Error deleting user');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.put(`/users/${user.id}/toggle-status`);
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.put(`/users/${selectedUser.id}/reset-password`, {
        newPassword
      });
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      alert(response.data.message + '\n\nNote: If the user is currently logged in, they must logout and login again to be prompted to change their password.');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(error.response?.data?.message || 'Error resetting password');
    }
  };

  const getRoleInfo = (role) => {
    const roleInfo = ROLES.find(r => r.value === role);
    // Return found role or a default fallback
    return roleInfo || { 
      value: role, 
      label: role.charAt(0).toUpperCase() + role.slice(1), 
      icon: '👤', 
      color: 'default' 
    };
  };

  const filteredUsers = users.filter((u) => {
    if (statusFilter === 'active' && !u.isActive) return false;
    if (statusFilter === 'inactive' && u.isActive) return false;
    if (roleFilter && !u.roles?.includes(roleFilter) && u.role !== roleFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (
        u.fullName?.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.employeeId?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleExportExcel = () => {
    const rows = filteredUsers.map((u) => ({
      'Employee ID': u.employeeId || '',
      'First Name': u.firstName || '',
      'Last Name': u.lastName || '',
      'Full Name': u.fullName || '',
      'Username': u.username || '',
      'Email': u.email || '',
      'Role': u.role || '',
      'Phone': u.phoneNumber || '',
      'WhatsApp': u.whatsappNumber || '',
      'Salary (INR)': u.salary || '',
      'Part-time': u.partTime ? 'Yes' : 'No',
      'Status': u.isActive ? 'Active' : 'Inactive',
      'Date of Joining': u.dateOfJoining ? u.dateOfJoining.toString().slice(0, 10) : '',
      'Last Working Date': u.lastWorkingDate ? u.lastWorkingDate.toString().slice(0, 10) : '',
      'Activated At': u.activatedAt ? new Date(u.activatedAt).toLocaleDateString('en-GB') : '',
      'Deactivated At': u.deactivatedAt ? new Date(u.deactivatedAt).toLocaleDateString('en-GB') : '',
      'Bank Account': u.bankAccountNumber || '',
      'IFSC Code': u.bankIfscCode || '',
      'Bank Name': u.bankName || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, `users_${statusFilter}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && !users.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          👥 User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={1} mb={2}>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Typography variant="caption" color="textSecondary">Total</Typography>
                <Typography variant="h4" fontWeight={700}>{statistics.totalUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Typography variant="caption" color="textSecondary">Active</Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">{statistics.activeUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Typography variant="caption" color="textSecondary">Inactive</Typography>
                <Typography variant="h4" fontWeight={700} color="error.main">{statistics.inactiveUsers}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Filter by Role</InputLabel>
            <Select
              value={roleFilter}
              label="Filter by Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              {ROLES.map(role => (
                <MenuItem key={role.value} value={role.value}>
                  {role.icon} {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={1}>
          <Button fullWidth variant="outlined" onClick={fetchUsers} sx={{ height: '56px' }}>
            Search
          </Button>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button
            fullWidth
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            sx={{ height: '56px' }}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {/* Desktop Table View */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Contact</TableCell>
              {canSeeSalary && <TableCell>Salary</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => {
              const roleInfo = getRoleInfo(user.role);
              // Safely handle roles - ensure it's always an array
              let userRoles = [];
              if (Array.isArray(user.roles) && user.roles.length > 0) {
                userRoles = user.roles;
              } else if (user.role) {
                userRoles = [user.role];
              }
              
              // Generate User ID as JBDE + First Name
              const userId = user.firstName ? `JBDE${user.firstName}` : `JBDE${user.username}`;
              
              return (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => { setSelectedUser(user); setViewDialogOpen(true); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {userId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}`
                          : user.fullName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        @{user.username}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {userRoles.length > 0 ? (
                        userRoles.map((role) => {
                          const info = getRoleInfo(role);
                          return (
                            <Chip
                              key={role}
                              label={`${info.icon} ${info.label}`}
                              color={info.color}
                              size="small"
                            />
                          );
                        })
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No roles assigned
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.phoneNumber && (
                      <Typography variant="caption" display="block">
                        📱 {user.phoneNumber}
                      </Typography>
                    )}
                    {user.whatsappNumber && (
                      <Typography variant="caption" display="block">
                        💬 {user.whatsappNumber}
                      </Typography>
                    )}
                    {!user.phoneNumber && !user.whatsappNumber && (
                      <Typography variant="caption" color="textSecondary">
                        No contact info
                      </Typography>
                    )}
                  </TableCell>
                  {canSeeSalary && (
                    <TableCell>
                      {user.salary ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {isOwner 
                              ? `₹${parseInt(user.salary).toLocaleString('en-IN')}`
                              : '₹••••••'
                            }
                          </Typography>
                          {!isOwner && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              (Hidden)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Not set
                        </Typography>
                      )}
                      {user.partTime && (
                        <Chip label="Part-time" size="small" color="info" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    {user.dateOfJoining && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        DOJ: {new Date(user.dateOfJoining).toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                    {!user.isActive && user.deactivatedAt && (
                      <Typography variant="caption" color="error.light" sx={{ display: 'block' }}>
                        Left: {new Date(user.deactivatedAt).toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                    {!user.isActive && user.lastWorkingDate && (
                      <Typography variant="caption" color="error.light" sx={{ display: 'block' }}>
                        LWD: {new Date(user.lastWorkingDate).toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => { setSelectedUser(user); setViewDialogOpen(true); }}
                      title="View Profile"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStatus(user)}
                      title={user.isActive ? 'Deactivate' : 'Activate'}
                      disabled={!user.isActive && !canReactivate}
                    >
                      {user.isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedUser(user);
                        setPasswordDialogOpen(true);
                      }}
                      title="Reset Password"
                    >
                      <LockIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(user)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    {isOwner && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteDialogOpen(true);
                        }}
                        title="Delete User (Owner Only)"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {filteredUsers.length === 0 ? (
          <Card>
            <CardContent>
              <Typography color="text.secondary" align="center">
                No users found. Click "Add User" to create one.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {filteredUsers.map((user) => {
              let userRoles = [];
              if (Array.isArray(user.roles) && user.roles.length > 0) {
                userRoles = user.roles;
              } else if (user.role) {
                userRoles = [user.role];
              }
              const userId = user.firstName ? `JBDE${user.firstName}` : `JBDE${user.username}`;
              
              return (
                <Card
                  key={user.id}
                  variant="outlined"
                  onClick={() => { setSelectedUser(user); setViewDialogOpen(true); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Typography variant="caption" color="primary" fontWeight="bold">
                          {userId}
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}`
                            : user.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{user.username}
                        </Typography>
                      </Box>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Stack spacing={1}>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {userRoles.map((role) => {
                          const info = getRoleInfo(role);
                          return (
                            <Chip
                              key={role}
                              label={`${info.icon} ${info.label}`}
                              color={info.color}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                      
                      <Divider />
                      
                      {user.phoneNumber && (
                        <Typography variant="body2">
                          📱 {user.phoneNumber}
                        </Typography>
                      )}
                      {user.whatsappNumber && (
                        <Typography variant="body2">
                          💬 {user.whatsappNumber}
                        </Typography>
                      )}

                      {user.dateOfJoining && (
                        <Typography variant="caption" color="text.secondary">
                          DOJ: {new Date(user.dateOfJoining).toLocaleDateString('en-GB')}
                        </Typography>
                      )}
                      {!user.isActive && user.deactivatedAt && (
                        <Typography variant="caption" color="error">
                          Left: {new Date(user.deactivatedAt).toLocaleDateString('en-GB')}
                        </Typography>
                      )}
                      {!user.isActive && user.lastWorkingDate && (
                        <Typography variant="caption" color="error">
                          LWD: {new Date(user.lastWorkingDate).toLocaleDateString('en-GB')}
                        </Typography>
                      )}
                      {user.isActive && user.activatedAt && (
                        <Typography variant="caption" color="success.main">
                          Activated: {new Date(user.activatedAt).toLocaleDateString('en-GB')}
                        </Typography>
                      )}

                      <Box display="flex" gap={0.5} justifyContent="flex-end" mt={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => { setSelectedUser(user); setViewDialogOpen(true); }}
                          title="View Profile"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(user)}
                          color={user.isActive ? 'success' : 'default'}
                          disabled={!user.isActive && !canReactivate}
                        >
                          {user.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUser(user);
                            setPasswordDialogOpen(true);
                          }}
                        >
                          <LockIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(user)}
                        >
                          <EditIcon />
                        </IconButton>
                        {isOwner && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteDialogOpen(true);
                            }}
                            title="Delete User (Owner Only)"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>

      {filteredUsers.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No users found. Create your first user!
        </Alert>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}
        maxWidth={dialogMaximized ? false : 'sm'}
        fullWidth={!dialogMaximized}
        fullScreen={dialogMaximized}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          {selectedUser ? 'Edit User' : 'Add New User'}
          <IconButton size="small" onClick={() => setDialogMaximized(m => !m)}
            title={dialogMaximized ? 'Minimize' : 'Expand'}>
            {dialogMaximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                required
                error={!formData.firstName}
                helperText={!formData.firstName ? 'First Name is required' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username *"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                InputProps={{
                  readOnly: true
                }}
                disabled={selectedUser ? true : false}
                helperText={!selectedUser ? 'Auto-generated: JBDE + First Name' : 'Username cannot be changed once created'}
                error={!formData.username}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            {!selectedUser && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth required error={!formData.roles || formData.roles.length === 0}>
                <InputLabel>Roles *</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(formData.roles) ? formData.roles : []}
                  onChange={(e) => {
                    const selectedRoles = e.target.value;
                    setFormData({ 
                      ...formData, 
                      roles: selectedRoles,
                      role: selectedRoles.length > 0 ? selectedRoles[0] : '' // Set first role as primary
                    });
                  }}
                  input={<OutlinedInput label="Roles *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(selected) && selected.length > 0 ? (
                        selected.map((value) => {
                          const roleInfo = getRoleInfo(value);
                          return (
                            <Chip
                              key={value}
                              label={`${roleInfo.icon} ${roleInfo.label}`}
                              size="small"
                              color={roleInfo.color}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="error">
                          Select at least one role...
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  {ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Checkbox checked={Array.isArray(formData.roles) && formData.roles.indexOf(role.value) > -1} />
                      <ListItemText primary={`${role.icon} ${role.label}`} />
                    </MenuItem>
                  ))}
                </Select>
                {(!formData.roles || formData.roles.length === 0) && (
                  <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                    At least one role is required
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number *"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="+1234567890"
                required
                error={!formData.phoneNumber}
                helperText={!formData.phoneNumber ? 'Phone Number is required' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp Number *"
                value={formData.whatsappNumber}
                onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                placeholder="+1234567890"
                required
                error={!formData.whatsappNumber}
                helperText={!formData.whatsappNumber ? 'WhatsApp Number is required' : ''}
              />
            </Grid>
            
            {/* Employee Information Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Employee Information" size="small" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={canSeeSalary ? 6 : 12}>
              <TextField
                fullWidth
                label="Employee ID"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                placeholder="JBD0011"
                helperText="Format: JBD0011, JBD0012, etc."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Joining"
                type="date"
                value={formData.dateOfJoining}
                onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Employee's joining date"
              />
            </Grid>
            {canSeeSalary && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Salary (Monthly)"
                  type={showSalary || isOwner ? "number" : "password"}
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  placeholder="26000"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: !isOwner && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSalary(!showSalary)}
                          edge="end"
                          size="small"
                          title={showSalary ? "Hide salary" : "Show salary"}
                        >
                          {showSalary ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  helperText={isOwner 
                    ? "Monthly salary in INR" 
                    : showSalary 
                      ? "Salary visible - Click toggle to hide"
                      : "Salary masked - Click toggle to view/edit"
                  }
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth select
                label="Gender"
                value={formData.gender || ''}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <Box display="flex" alignItems="center" gap={1}>
                  <Checkbox
                    checked={formData.partTime}
                    onChange={(e) => setFormData({ ...formData, partTime: e.target.checked })}
                    color="primary"
                  />
                  <Typography variant="body1">
                    Part-time Employee
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                  Check this box if the employee works part-time
                </Typography>
              </FormControl>
            </Grid>

            {formData.partTime && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Working Days / Month"
                  type="number"
                  value={formData.partTimeDaysPerMonth}
                  onChange={(e) => setFormData({ ...formData, partTimeDaysPerMonth: e.target.value })}
                  placeholder="e.g. 12"
                  inputProps={{ min: 1, max: 31 }}
                  helperText="Tentative working days per month for this part-time employee"
                />
              </Grid>
            )}

            {/* Emergency Contact Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Emergency Contact" size="small" color="error" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergencyContactName}
                onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                placeholder="John Doe"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Mobile"
                value={formData.emergencyContactMobile}
                onChange={(e) => setFormData({ ...formData, emergencyContactMobile: e.target.value })}
                placeholder="+9876543210"
              />
            </Grid>
            
            {/* Bank Account Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="🏦 Bank Account Details (Salary)" size="small" color="primary" />
              </Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={formData.accountHolderName}
                onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                placeholder="As per bank account"
                helperText="Name as it appears on the bank account"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={formData.bankAccountNumber}
                onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                placeholder="1234567890123456"
                helperText="Bank account number for salary deposit"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IFSC Code"
                value={formData.bankIfscCode}
                onChange={(e) => setFormData({ ...formData, bankIfscCode: e.target.value.toUpperCase() })}
                placeholder="SBIN0001234"
                helperText="Bank IFSC code (11 characters)"
                inputProps={{ maxLength: 11 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                placeholder="State Bank of India"
                helperText="Name of the bank"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Branch (Optional)"
                value={formData.bankBranch}
                onChange={(e) => setFormData({ ...formData, bankBranch: e.target.value })}
                placeholder="Main Branch, City"
                helperText="Branch name/location (optional)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}
                >
                  <MenuItem value={true}>✅ Active</MenuItem>
                  <MenuItem value={false}>❌ Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Working Date"
                type="date"
                value={formData.lastWorkingDate}
                onChange={(e) => setFormData({ ...formData, lastWorkingDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={formData.isActive === true}
                helperText={formData.isActive ? 'Only settable when status is Inactive' : "Employee's last day at work"}
              />
            </Grid>

            {/* Re-joinee — relieving & re-joining history */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Relieving & Re-joining" size="small" /></Divider>
              <FormControlLabel
                control={<Checkbox checked={!!formData.isRejoinee} onChange={(e) => setFormData({ ...formData, isRejoinee: e.target.checked })} />}
                label="Re-joinee (worked at JBDE before)"
              />
              {formData.isRejoinee && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Record each previous stint (before the current Date of Joining, which is the latest re-joining date).
                  </Typography>
                  {parseHistory(formData.employmentHistory).map((s, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" type="date" label="Joined On" InputLabelProps={{ shrink: true }}
                            value={s.joinedOn || ''} onChange={(e) => updateStint(i, 'joinedOn', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" type="date" label="Relieved On" InputLabelProps={{ shrink: true }}
                            value={s.relievedOn || ''} onChange={(e) => updateStint(i, 'relievedOn', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" label="Designation" placeholder="e.g. Editing"
                            value={s.designation || ''} onChange={(e) => updateStint(i, 'designation', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth size="small" label="Reason for Leaving"
                            value={s.relievingReason || ''} onChange={(e) => updateStint(i, 'relievingReason', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
                          <IconButton color="error" size="small" onClick={() => removeStint(i)}><DeleteIcon fontSize="small" /></IconButton>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Remarks (optional)"
                            value={s.remarks || ''} onChange={(e) => updateStint(i, 'remarks', e.target.value)} />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={addStint}>Add Previous Stint</Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>⚠️ Confirm Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete user "{selectedUser?.fullName}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            ⚠️ WARNING: This action cannot be undone!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The user and all their data will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Profile (read-only) */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User / Employee Profile</DialogTitle>
        <DialogContent dividers>
          {selectedUser && (() => {
            const u = selectedUser;
            const rolesList = Array.isArray(u.roles) ? u.roles : (() => { try { return JSON.parse(u.roles || '[]'); } catch { return []; } })();
            const allRoles = [...new Set([u.role, ...rolesList].filter(Boolean))];
            const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
            const money = (n) => (n != null && n !== '' ? `₹${Number(n).toLocaleString('en-IN')}` : 'Not set');
            const history = parseHistory(u.employmentHistory);
            const isRejoinee = !!u.isRejoinee || history.length > 0;
            const rows = [
              ['Employee ID', u.employeeId || '—'],
              ['Username', u.username ? `@${u.username}` : '—'],
              ['Designation', u.designation || '—'],
              ['Email', u.email || '—'],
              ['Contact', u.phoneNumber || '—'],
              ['WhatsApp', u.whatsappNumber || '—'],
              ['Gender', u.gender || '—'],
              ...(canSeeSalary ? [['Salary (monthly)', money(u.salary)]] : []),
              ['Date of Joining', fmtDate(u.dateOfJoining)],
              ['Status', u.isActive ? 'Active' : 'Inactive'],
              ['Employee Type', u.partTime ? `Part-time (${u.partTimeDaysPerMonth || '?'} days/mo)` : 'Full-time'],
              ['Emergency Contact', u.emergencyContactName ? `${u.emergencyContactName} (${u.emergencyContactMobile || '—'})` : '—'],
              ...(canSeeSalary ? [
                ['Bank', u.bankName ? `${u.bankName}${u.bankBranch ? ' — ' + u.bankBranch : ''}` : '—'],
                ['Account No.', u.bankAccountNumber || '—'],
                ['IFSC', u.bankIfscCode || '—'],
              ] : []),
            ];
            return (
              <Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="h6" fontWeight={700}>{u.fullName || u.username}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {allRoles.map((r) => <Chip key={r} size="small" label={r} sx={{ textTransform: 'capitalize' }} />)}
                    <Chip size="small" color={u.isActive ? 'success' : 'default'} label={u.isActive ? 'Active' : 'Inactive'} />
                    {isRejoinee && <Chip size="small" color="warning" label="Re-joinee" />}
                  </Box>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={1.2}>
                  {rows.map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {isRejoinee && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Re-joinee — Previous Stint{history.length !== 1 ? 's' : ''}
                    </Typography>
                    {history.length === 0 && (
                      <Typography variant="body2" color="text.secondary">Marked as re-joinee; no prior stint details recorded.</Typography>
                    )}
                    {history.map((s, i) => (
                      <Box key={i} sx={{ mb: 1, pl: 1.2, borderLeft: '3px solid', borderColor: 'warning.light' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {fmtDate(s.joinedOn)} → {fmtDate(s.relievedOn)}{s.designation ? ` · ${s.designation}` : ''}
                        </Typography>
                        {s.relievingReason && <Typography variant="caption" color="text.secondary" display="block">Reason: {s.relievingReason}</Typography>}
                        {s.remarks && <Typography variant="caption" color="text.secondary" display="block">{s.remarks}</Typography>}
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      Current stint (re-joined): <strong>{fmtDate(u.dateOfJoining)}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
